import { getPrestationTypeMeta } from './prestation-types.js';
import { getSpaceUiConfig } from '../components/prestation-wizard/spaceLabels.js';
import { shouldShowInstructeurFeature, getInstructeurLabels } from '../utils/instructeurLabels.js';

const BASE = {
  headerTitle: 'Informations de l’activité',
  headerSubtitle: 'Décrivez précisément ce que vos clients réservent',
  nomLabel: 'Nom de l’activité *',
  nomPlaceholder: 'Indiquez le nom affiché sur le site',
  descriptionPlaceholder: 'Décrivez le déroulé, les bénéfices, ce qui est inclus…',
  durationSectionTitle: 'Durée & capacité',
  durationSectionSubtitle: 'Durée de la séance et nombre de participants',
  dureeLabel: 'Durée (minutes) *',
  capacityLabel: 'Capacité maximale',
  capacityHint: 'Nombre maximum de personnes pour cette activité.',
  prixPlaceholder: 'Ex. : 65',
  preparationTitle: 'Préparation client',
  preparationSubtitle: 'Matériel et consignes avant la séance',
  showMultiDurees: false,
  showRessourceLink: false,
  showTarifsDuree: false,
  showEquipement: true,
  showCreneaux: true,
  showOptions: true,
  showInstructeur: false,
  footerHint:
    'Après enregistrement, l’activité sera disponible côté client et dans votre calendrier.',
};

const BY_TYPE = {
  CRENEAUX: {
    headerSubtitle: 'Créneau horaire sur un terrain, une salle ou un espace',
    nomPlaceholder: 'Ex. : Session padel 1h, Location terrain foot…',
    descriptionPlaceholder:
      'Précisez l’espace, le niveau requis, ce qui est inclus dans le créneau…',
    durationSectionTitle: 'Durée, capacité & espace',
    durationSectionSubtitle: 'Durées proposées, lieu et tarifs par durée',
    capacityLabel: 'Participants max. par créneau',
    capacityHint: 'Ex. : 4 pour un double padel, 10 pour un cours collectif sur terrain.',
    showMultiDurees: true,
    showRessourceLink: false,
    showTarifsDuree: true,
    creneauxTitle: 'Disponibilité',
    creneauxSubtitle: 'Plages horaires ou horaires boutique — créneaux générés automatiquement',
    showCreneauxQuickSetup: true,
    footerHint:
      'Après enregistrement, configurez les créneaux pour que les clients puissent réserver en ligne.',
  },
  RENDEZ_VOUS: {
    headerSubtitle: 'Rendez-vous ou prestation avec un praticien / moniteur',
    nomPlaceholder: 'Ex. : Balade à cheval 60 min, Coupe + brushing, Cours particulier…',
    descriptionPlaceholder:
      'Décrivez le déroulé, le public visé, ce que le client vivra pendant le rendez-vous…',
    durationSectionTitle: 'Durée & participants',
    durationSectionSubtitle: 'Durée du rendez-vous et taille du groupe',
    capacityLabel: 'Nombre de participants',
    capacityHint: 'Indiquez 1 pour un rendez-vous individuel, plus pour un atelier ou une balade de groupe.',
    showMultiDurees: false,
    showRessourceLink: false,
    showTarifsDuree: false,
    creneauxTitle: 'Disponibilité',
    creneauxSubtitle: 'Par défaut : horaires d’ouverture de la boutique (créneaux automatiques)',
    showCreneauxQuickSetup: true,
    showInstructeur: false,
    footerHint:
      'Après enregistrement, ajoutez les horaires de disponibilité pour ouvrir les réservations.',
  },
  RESERVATION: {
    headerSubtitle: 'Réservation de table ou de couvert au restaurant',
    nomPlaceholder: 'Ex. : Table terrasse, Menu déjeuner, Soirée groupe…',
    descriptionPlaceholder: 'Ambiance, formules, conditions de réservation…',
    durationSectionTitle: 'Durée & couverts',
    durationSectionSubtitle: 'Temps de table et nombre de places',
    capacityLabel: 'Nombre de couverts max.',
    capacityHint: 'Capacité maximale pour cette formule de réservation.',
    showMultiDurees: false,
    showRessourceLink: false,
    showTarifsDuree: false,
    showEquipement: false,
    showCreneaux: false,
    showOptions: false,
    creneauxTitle: 'Disponibilités',
    creneauxSubtitle: '',
    footerHint: 'Après enregistrement, l’offre sera visible pour la réservation de table.',
  },
};

/** Terrains / salles : padel, tennis, foot, five… — pas les RDV coiffure/soin. */
const COURT_SPORT_CATEGORIES = new Set([
  'PADEL',
  'TENNIS',
  'FOOT',
  'FIVE',
  'FUTSAL',
  'KARTING',
  'SQUASH',
  'BADMINTON',
]);

export function shouldShowRessourceLink(typePrestation, commercant) {
  if (typePrestation !== 'CRENEAUX') return false;
  const cat = String(commercant?.categorie || '').toUpperCase();
  return COURT_SPORT_CATEGORIES.has(cat);
}

export function getPrestationModalUi(typePrestation, commercant) {
  const type = typePrestation || 'CRENEAUX';
  const meta = getPrestationTypeMeta(type);
  const spaceLabels = getSpaceUiConfig(commercant?.categorie);
  const merged = { ...BASE, ...(BY_TYPE[type] || BY_TYPE.CRENEAUX) };

  if (shouldShowRessourceLink(type, commercant)) {
    merged.showRessourceLink = true;
    merged.ressourceLabel = `${spaceLabels.spaceSingular} lié à cette activité`;
    merged.ressourceHint =
      'Utile quand chaque activité correspond à un terrain précis (Terrain 1, Court A…). Sinon laissez vide : le client choisit le terrain disponible.';
  }

  if (shouldShowInstructeurFeature(commercant?.categorie)) {
    const instructeurLabels = getInstructeurLabels(commercant?.categorie);
    merged.showInstructeur = type !== 'RESERVATION';
    merged.instructeurAssignLabel = instructeurLabels.assignLabel;
    merged.instructeurAssignHint = instructeurLabels.assignHint;
    merged.instructeurEmptyHint = instructeurLabels.emptyListHint;
  }

  return {
    ...merged,
    type,
    typeLabel: meta?.label || type,
    typeIcon: meta?.icon || '📋',
    typeHint: meta?.hint || '',
  };
}

/** Defaults appliqués à la création quand on change de type */
export function getPrestationTypeSwitchPatch(newType, prevForm, commercant) {
  const patch = {};
  if (newType === 'RENDEZ_VOUS') {
    patch.dureesAutoriseesMinutes = [];
    patch.tarifsDuree = [];
    if (!prevForm.nom && (prevForm.maxParticipants ?? 1) > 6) {
      patch.maxParticipants = 1;
    } else if ((prevForm.maxParticipants ?? 1) > 12) {
      patch.maxParticipants = Math.min(prevForm.maxParticipants, 12);
    }
    patch.ressourceId = null;
  }
  if (newType === 'CRENEAUX') {
    const cat = String(commercant?.categorie || '').toUpperCase();
    if ((prevForm.maxParticipants ?? 1) <= 1 && ['PADEL', 'TENNIS', 'FOOT', 'FIVE', 'FUTSAL'].includes(cat)) {
      patch.maxParticipants = ['FOOT', 'FIVE', 'FUTSAL'].includes(cat) ? 10 : 4;
    }
    if (!shouldShowRessourceLink('CRENEAUX', commercant)) {
      patch.ressourceId = null;
    }
  }
  if (newType === 'RESERVATION') {
    patch.dureesAutoriseesMinutes = [];
    patch.tarifsDuree = [];
    patch.ressourceId = null;
    patch.equipementFourni = false;
    patch.equipementRequis = [];
    patch.equipementFourniGratuit = [];
    patch.equipementFourniPayant = [];
  }
  return patch;
}
